/**
 * Lưu / tải / migrate — hàm thuần, KHÔNG chạm localStorage
 * (lớp ngoài inject storage, xem src/luu-tru.ts).
 *
 * Định dạng: { phienBan, lucLuu, trangThai }. Migrate từng bước:
 * MIGRATIONS[n] biến bản lưu n thành n+1.
 */
import type { TrangThaiGame } from './types.js';

/** Phiên bản định dạng bản lưu hiện tại. */
export const PHIEN_BAN_LUU_TRU = 3;

export interface BanLuu {
  phienBan: number;
  lucLuu: string;
  trangThai: TrangThaiGame;
}

type HamMigrate = (trangThai: Record<string, unknown>) => Record<string, unknown>;

/**
 * Các bước migrate. Bản 2 thêm bố cục nội thất + tutorial (M2).
 * Bản 3 thêm toàn bộ trạng thái M3: khu vực, nhiệm vụ, đơn lớn, NPC,
 * thời tiết, thành tựu, xu cao cấp, sưu tập, cosmetic, nhân viên,
 * cà phê theo món, cài đặt trong game.
 */
const MIGRATIONS: Record<number, HamMigrate> = {
  1: (s) => ({ ...s, boCuc: [], uidNoiThatTiep: 0, tutorialXong: false }),
  2: (s) => ({
    ...s,
    khuHienTai: 'pho-cho',
    nhiemVuNgay: '',
    nhiemVu: [],
    donLon: null,
    npcThanThiet: {},
    npcQuaDaTang: {},
    thoiTiet: {},
    thanhTuuDaMo: [],
    xuCaoCap: 0,
    suuTapXong: [],
    cosmeticSoHuu: [],
    nhanVien: { thuNgan: false, xepKe: false },
    caPheTheoMon: {},
    caiDat: {
      nhacBat: true,
      sfxBat: true,
      giamChuyenDong: false,
      hieuUngBat: true,
      coChu: 'vua',
      tatRung: false,
    },
    thongKe: {
      ...(s['thongKe'] as Record<string, unknown>),
      soDonLonHoanThanh: 0,
      tongCocCaPhe: 0,
      soQuaTang: 0,
    },
  }),
};

/** Đóng gói state thành bản lưu. */
export function taoBanLuu(state: TrangThaiGame): BanLuu {
  return {
    phienBan: PHIEN_BAN_LUU_TRU,
    lucLuu: new Date().toISOString(),
    trangThai: JSON.parse(JSON.stringify(state)) as TrangThaiGame,
  };
}

function laBanLuuHopLe(v: unknown): v is BanLuu {
  if (typeof v !== 'object' || v === null) return false;
  const b = v as Record<string, unknown>;
  if (typeof b['phienBan'] !== 'number') return false;
  const t = b['trangThai'] as Record<string, unknown> | undefined;
  if (typeof t !== 'object' || t === null) return false;
  return (
    typeof t['tongGioGame'] === 'number' &&
    typeof t['xu'] === 'number' &&
    typeof t['capCuaHang'] === 'number' &&
    typeof t['kho'] === 'object' &&
    t['kho'] !== null &&
    typeof t['nguoiChoi'] === 'object' &&
    t['nguoiChoi'] !== null
  );
}

/** Migrate bản lưu bất kỳ về phiên bản hiện tại. Ném lỗi nếu không đọc được. */
export function taiBanLuu(v: unknown): TrangThaiGame {
  if (!laBanLuuHopLe(v)) {
    throw new Error('Bản lưu không hợp lệ: thiếu phienBan hoặc trường bắt buộc.');
  }
  let phienBan = v.phienBan;
  let trangThai: Record<string, unknown> = v.trangThai as unknown as Record<string, unknown>;
  while (phienBan < PHIEN_BAN_LUU_TRU) {
    const buoc = MIGRATIONS[phienBan];
    if (!buoc) throw new Error(`Không có bước migrate từ phiên bản ${phienBan}.`);
    trangThai = buoc(trangThai);
    phienBan += 1;
  }
  if (phienBan > PHIEN_BAN_LUU_TRU) {
    throw new Error(
      `Bản lưu phiên bản ${phienBan} mới hơn game (${PHIEN_BAN_LUU_TRU}) — cập nhật game.`,
    );
  }
  return trangThai as unknown as TrangThaiGame;
}

/** Xuất bản lưu thành chuỗi JSON (người chơi copy / tải file). */
export function xuatJson(state: TrangThaiGame): string {
  return JSON.stringify(taoBanLuu(state));
}

/** Nhập từ chuỗi JSON đã xuất. Ném lỗi nếu sai định dạng. */
export function nhapJson(json: string): TrangThaiGame {
  let v: unknown;
  try {
    v = JSON.parse(json) as unknown;
  } catch {
    throw new Error('Chuỗi nhập không phải JSON hợp lệ.');
  }
  return taiBanLuu(v);
}
